import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import {
  getDataUnitsByFacility,
  associateDeviceIdentifier,
  disassociateDeviceIdentifier,
} from '../services/apiServices';
import { useFeedbackContext } from '../context/FeedbackContext';
import { ACTION_TYPES } from '../constants/actionTypes';
import { serviceLogger } from '../utils/serviceLogger';

/**
 * Custom hook to handle the association/disassociation of device identifiers for a facility.
 *
 * @param {Object} facility - The current facility (should have an id in "Edit" mode).
 * @param {string} action - Either 'Add' or 'Edit' (ACTION_TYPES.ADD / ACTION_TYPES.EDIT)
 * @returns {Object} - An object containing the device identifier state, setters, UI flags, and the handleDeviceAssociation function.
 */
const useDeviceAssociation = (facility, action) => {
  const [deviceIdentifier, setDeviceIdentifier] = useState([]);
  const [oldDeviceIdentifier, setOldDeviceIdentifier] = useState('');
  const [showDeviceToggle, setShowDeviceToggle] = useState(false);
  const [showDeviceInput, setShowDeviceInput] = useState(false);
  const { showToast } = useFeedbackContext();

  // Fetch device data when the modal loads (only for Edit mode)
  useEffect(() => {
    const fetchDeviceData = async () => {
      if (action === ACTION_TYPES.EDIT && facility?.id) {
        try {
          const dataUnits = await getDataUnitsByFacility(facility.id);
          if (_.isArray(dataUnits) && !_.isEmpty(dataUnits)) {
            setDeviceIdentifier(dataUnits);
            setOldDeviceIdentifier(dataUnits);
            setShowDeviceInput(true);
            setShowDeviceToggle(false);
          } else {
            setShowDeviceInput(false);
            setShowDeviceToggle(true);
          }
        } catch (error) {
          // If a 404 error occurs, assume no device is associated
          if (error.response?.status === 404) {
            setShowDeviceInput(false);
            setShowDeviceToggle(true);
          } else {
            serviceLogger.error('Error fetching device data units:', error);
          }
        }
      } else if (action === ACTION_TYPES.ADD) {
        setShowDeviceToggle(true);
        setShowDeviceInput(false);
      }
    };

    fetchDeviceData();
  }, [action, facility]);

  /**
   * Handles the association/disassociation of the device identifier.
   * This function should be called after the facility is saved.
   *
   * @param {number} [savedFacilityId] - An optional facility ID from the saved facility.
   */
  const handleDeviceAssociation = useCallback(
    async (savedFacilityId, newDeviceIdentifier) => {
      const cleanNew = newDeviceIdentifier.filter(Boolean).map((mac) => mac.trim().toUpperCase());

      const oldMacs = oldDeviceIdentifier.map(({ esp32_mac_address }) =>
        esp32_mac_address.toUpperCase(),
      );

      const newSet = new Set(cleanNew);
      const oldSet = new Set(oldMacs);

      const macsThatAreNew = cleanNew.filter((mac) => !oldSet.has(mac));
      const macsToRemove = oldMacs.filter((mac) => !newSet.has(mac));

      if (!macsThatAreNew.length && !macsToRemove.length) return;

      try {
        const facilityId = savedFacilityId || facility?.id;

        await Promise.all(macsToRemove.map((mac) => disassociateDeviceIdentifier(facilityId, mac)));

        await Promise.all(macsThatAreNew.map((mac) => associateDeviceIdentifier(facilityId, mac)));

        if (macsThatAreNew.length) {
          showToast({
            variant: 'success',
            title: 'Device Updated',
            copy: `${
              macsThatAreNew.length === 1 ? '1 device' : `${macsThatAreNew.length} devices`
            } successfully associated.`,
          });
        }
        if (macsToRemove.length) {
          showToast({
            variant: 'success',
            title: 'Device Removed',
            copy: `${
              macsToRemove.length === 1 ? '1 device' : `${macsToRemove.length} devices`
            } successfully disassociated.`,
          });
        }

        setOldDeviceIdentifier(
          cleanNew.map((mac) => ({ esp32_mac_address: mac, is_active: true })),
        );
      } catch (error) {
        serviceLogger.error('Error updating device association:', error);
        showToast({
          variant: 'error',
          title: 'Device Error',
          copy: 'There was a problem associating/disassociating the device.',
        });
      }
    },
    [facility, oldDeviceIdentifier, showToast],
  );

  return {
    deviceIdentifier,
    setDeviceIdentifier,
    showDeviceInput,
    setShowDeviceInput,
    showDeviceToggle,
    handleDeviceAssociation,
  };
};

export default useDeviceAssociation;
